'use client';

import * as React from 'react';
import { Icon } from '../../atomics/Icon/Icon';
import type { IconName } from '../../atomics/Icon/Icon';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  resolveHashNavActiveValue,
  tabsUseHashHrefs,
} from '@/hen-ui/utils/navHash';
import { scrollAnchorIntoView } from '@/hen-ui/utils/scrollAnchor';
import { Tabs as TabsRoot, TabsList, TabsTrigger } from '../../shadcn/tabs';
import {
  navTabsLabelVariants,
  navTabsListVariants,
  navTabsTriggerVariants,
} from './NavTabs.variants';

export interface NavTabItem {
  value: string;
  label: React.ReactNode;
  href: string;
  /** Lucide icon name (e.g. "calculator", "map"). */
  icon?: IconName;
  disabled?: boolean;
}

export interface NavTabsProps {
  tabs: NavTabItem[];
  /**
   * Which tab looks selected. Omit for hash-link tabs (`href="#..."`) — active
   * state follows `location.hash`. For route tabs, defaults to the first tab.
   */
  activeValue?: string;
  /** Card tabs (default) or underline link tabs. */
  variant?: 'default' | 'underline';
  /**
   * Root element for each tab: Radix `TabsTrigger` uses `asChild`, so Slot merges trigger props
   * (including `className`, `data-state`, focus handlers) onto this component. It also receives
   * `href` and passive `data-hen-id` (`hen.navTabs`) for catalog identity.
   *
   * **Contract:** Must forward unknown props (especially `data-*`) and `ref` to the underlying `<a>`
   * (e.g. Next.js `Link` does this). Wrappers that strip extra props will drop `data-hen-id` and break
   * catalog detection; prefer a plain `<a>` or a link primitive that spreads `...rest` onto the DOM node.
   */
  linkComponent?: React.ElementType;
  className?: string;
}

function useLocationHash(): string {
  const [hash, setHash] = React.useState('');

  React.useEffect(() => {
    const readHash = () => setHash(window.location.hash);
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  return hash;
}

function resolveActiveValue(
  tabs: NavTabItem[],
  activeValue: string | undefined,
  hash: string,
): string {
  if (activeValue !== undefined) return activeValue;
  if (tabsUseHashHrefs(tabs)) return resolveHashNavActiveValue(tabs, hash);
  return tabs[0]?.value ?? '';
}

/** Tab highlighting matches hashes case-insensitively, so the scroll target lookup does too. */
function findHashTarget(id: string, doc: Document): HTMLElement | null {
  const exact = doc.getElementById(id);
  if (exact) return exact;
  const lowered = id.toLowerCase();
  return (
    Array.from(doc.querySelectorAll<HTMLElement>('[id]')).find(
      (element) => element.id.toLowerCase() === lowered,
    ) ?? null
  );
}

/**
 * `doc` is the tab's own document: a preview frame renders the tabs into an
 * iframe from the parent window, where the global `document` has no targets.
 * The new URL is built from the document's own URL because an `about:blank`
 * frame resolves a bare `#hash` against the parent's base URL, which
 * `replaceState` rejects.
 */
function scrollHashTarget(href: string, doc: Document): boolean {
  if (!href.startsWith('#')) return false;
  const id = href.slice(1);
  if (!id) return false;
  const target = findHashTarget(id, doc);
  if (!target) return false;
  scrollAnchorIntoView(target);
  const view = doc.defaultView;
  if (view && view.location.hash !== href) {
    const url = new URL(doc.URL);
    url.hash = href;
    view.history.replaceState(null, '', url.href);
    view.dispatchEvent(new view.HashChangeEvent('hashchange'));
  }
  return true;
}

function isPlainActivation(
  event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
): boolean {
  if ('key' in event) {
    return event.key === 'Enter' || event.key === ' ';
  }
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey
  );
}

function activateHashTarget(
  event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  href: string,
  disabled?: boolean,
) {
  if (!isPlainActivation(event)) return;
  if (disabled) {
    if (href.startsWith('#')) event.preventDefault();
    return;
  }
  if (!scrollHashTarget(href, event.currentTarget.ownerDocument)) return;
  event.preventDefault();
}

/**
 * Navigation tabs backed by Radix UI Tabs primitives.
 * Each tab is a navigable link — pass `linkComponent={Link}` for client-side routing.
 *
 * Hash `href`s scroll the matching element into view, below a docked top
 * sticky bar, and sync `location.hash`.
 * The active underline follows that hash when `activeValue` is omitted.
 * Route tabs should pass `activeValue` (or rely on the first tab).
 */
export function NavTabs({
  tabs,
  activeValue,
  variant = 'default',
  linkComponent: LinkComponent = 'a',
  className,
}: NavTabsProps) {
  const hash = useLocationHash();

  if (tabs.length === 0) return null;

  const resolvedActiveValue = resolveActiveValue(tabs, activeValue, hash);

  return (
    <TabsRoot value={resolvedActiveValue} className={className}>
      <TabsList className={navTabsListVariants({ variant })}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            asChild
            className={navTabsTriggerVariants({ variant })}
          >
            <LinkComponent
              href={tab.href}
              data-hen-id={COMPONENT_IDS.NavTabs}
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                activateHashTarget(event, tab.href, tab.disabled)
              }
              onKeyDown={(event: React.KeyboardEvent<HTMLElement>) =>
                activateHashTarget(event, tab.href, tab.disabled)
              }
            >
              {variant !== 'underline' && tab.icon && (
                <span className='flex h-8 w-8 shrink-0 items-center justify-center'>
                  <Icon
                    icon={tab.icon}
                    size='lg'
                    color={
                      resolvedActiveValue === tab.value ? 'primary' : 'default'
                    }
                    aria-hidden
                  />
                </span>
              )}
              <span className={navTabsLabelVariants({ variant })}>
                {tab.label}
              </span>
            </LinkComponent>
          </TabsTrigger>
        ))}
      </TabsList>
    </TabsRoot>
  );
}
